import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "sonner";
import Landing from "./pages/Landing";
import Account from "./pages/Account";
import { CustomerLogin, CustomerRegister, ForgotPassword, ResetPassword } from "./pages/CustomerAuth";
import { ForumIndex, ForumTopic } from "./pages/Forum";
import ProductPage from "./pages/ProductPage";
import BundlePage from "./pages/BundlePage";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import CheckoutPage from "./pages/CheckoutPage";
import CheckoutCancel from "./pages/CheckoutCancel";
import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import CookiePolicy from "./pages/legal/CookiePolicy";
import TermsAndConditions from "./pages/legal/TermsAndConditions";
import ModuloRecesso from "./pages/legal/ModuloRecesso";
import RecessoOnline from "./pages/legal/RecessoOnline";
import CookieBanner from "./components/leovera/CookieBanner";
import Analytics from "./components/leovera/Analytics";
import PageViewTracker from "./components/leovera/PageViewTracker";
import CartDrawer from "./components/leovera/CartDrawer";
import ChatWidget from "./components/leovera/ChatWidget";
import { AdminAuthProvider } from "./context/AdminAuthContext";
import { AuthProvider } from "./context/AuthContext";
import { WishlistProvider } from "./context/WishlistContext";
import { CartProvider } from "./context/CartContext";
import { ChatProvider } from "./context/ChatContext";
import { ThemeProvider } from "./context/ThemeContext";

// Admin bundle splitting: heavy admin pages load in separate chunks
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminCustomers = lazy(() => import("./pages/admin/AdminCustomers"));
const AdminForum = lazy(() => import("./pages/admin/AdminForum"));
const AdminDiscounts = lazy(() => import("./pages/admin/AdminDiscounts"));
const AdminBundles = lazy(() => import("./pages/admin/AdminBundles"));
const AdminTheme = lazy(() => import("./pages/admin/AdminTheme"));
const AdminEmails = lazy(() => import("./pages/admin/AdminEmails"));
const AdminInventory = lazy(() => import("./pages/admin/AdminInventory"));
const AdminSuppliers = lazy(() => import("./pages/admin/AdminSuppliers"));
const AdminLots = lazy(() => import("./pages/admin/AdminLots"));
const AdminTraceability = lazy(() => import("./pages/admin/AdminTraceability"));
const AdminBackups = lazy(() => import("./pages/admin/AdminBackups"));
const AdminPR = lazy(() => import("./pages/admin/AdminPR"));
const AdminReviews = lazy(() => import("./pages/admin/AdminReviews"));
const AdminRecessi = lazy(() => import("./pages/admin/AdminRecessi"));
const AdminStats = lazy(() => import("./pages/admin/AdminStats"));
const AdminAbandonedCarts = lazy(() => import("./pages/admin/AdminAbandonedCarts"));

const ChunkLoader = () => (
  <div className="min-h-screen bg-[var(--lv-bg,#060A14)] flex items-center justify-center">
    <Loader2 className="w-6 h-6 text-[#C0C0C0] animate-spin" />
  </div>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <WishlistProvider>
            <CartProvider>
              <ChatProvider>
                <AdminAuthProvider>
                <Suspense fallback={<ChunkLoader />}>
                <Routes>
                  {/* Public site */}
                  <Route path="/" element={<Landing />} />
                  <Route path="/leovera-nutrition-v103" element={<Landing />} />
                  <Route path="/prodotto/:slug" element={<ProductPage />} />
                  <Route path="/offerta/:slug" element={<BundlePage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/checkout/success" element={<CheckoutSuccess />} />
                  <Route path="/checkout/cancel" element={<CheckoutCancel />} />

                  {/* Customer auth + account */}
                  <Route path="/login" element={<CustomerLogin />} />
                  <Route path="/register" element={<CustomerRegister />} />
                  <Route path="/password-dimenticata" element={<ForgotPassword />} />
                  <Route path="/reset-password/:token" element={<ResetPassword />} />
                  <Route path="/account" element={<Account />} />

                  {/* Forum */}
                  <Route path="/forum" element={<ForumIndex />} />
                  <Route path="/forum/:slug" element={<ForumTopic />} />

                  {/* Legal (GDPR compliance) */}
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/cookie-policy" element={<CookiePolicy />} />
                  <Route path="/termini" element={<TermsAndConditions />} />
                  <Route path="/modulo-recesso" element={<ModuloRecesso />} />
                  <Route path="/recesso" element={<RecessoOnline />} />
                  <Route path="/terms" element={<TermsAndConditions />} />

                  {/* Admin */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="orders" element={<AdminOrders />} />
                    <Route path="products" element={<AdminProducts />} />
                    <Route path="users" element={<AdminUsers />} />
                    <Route path="customers" element={<AdminCustomers />} />
                    <Route path="pr" element={<AdminPR />} />
                    <Route path="forum" element={<AdminForum />} />
                    <Route path="discounts" element={<AdminDiscounts />} />
                    <Route path="bundles" element={<AdminBundles />} />
                    <Route path="theme" element={<AdminTheme />} />
                    <Route path="emails" element={<AdminEmails />} />
                    <Route path="inventory" element={<AdminInventory />} />
                    <Route path="suppliers" element={<AdminSuppliers />} />
                    <Route path="lots" element={<AdminLots />} />
                    <Route path="traceability" element={<AdminTraceability />} />
                    <Route path="backups" element={<AdminBackups />} />
                    <Route path="reviews" element={<AdminReviews />} />
                    <Route path="recessi" element={<AdminRecessi />} />
                    <Route path="stats" element={<AdminStats />} />
                    <Route path="abandoned-carts" element={<AdminAbandonedCarts />} />
                  </Route>

                  <Route path="*" element={<NotFound />} />
                </Routes>
                </Suspense>
                <CartDrawer />
                <ChatWidget />
                <CookieBanner />
                <Analytics />
                <PageViewTracker />
                <Toaster
                  position="bottom-center"
                  theme="dark"
                  toastOptions={{
                    style: {
                      background: "#0A1121",
                      border: "1px solid rgba(192,192,192,0.35)",
                      color: "#E5E4E2",
                    },
                  }}
                />
              </AdminAuthProvider>
            </ChatProvider>
          </CartProvider>
          </WishlistProvider>
        </AuthProvider>
      </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
